import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { toast } from "./ui/sonner";
import { RotateCcw, Loader2, AlertTriangle } from "lucide-react";

/**
 * 退回设计部确认弹窗（工程部 → 设计部）。
 *
 * 需求背景：项目由设计部移交到工程部后，若工程部发现尚未拿到 Permit（当初可能是强制移交进来的）
 *   或图纸未就绪，就无法开工；此时开工倒计时卡会一直把它标成「紧急 · 无动作」并短信催办。
 *   本弹窗提供「退回上一部门」入口，把项目退回设计部，等补齐 Permit 后由设计部重新移交。
 *
 * 交互要点：
 *   - 退回原因必填（后端同样强校验），用于设计部追溯「为什么被退回来」；
 *   - 提交后调用 POST /kickoff/{id}/return-design：工程侧停止倒计时与短信催办，设计侧工作状态退回「建筑审图中」。
 *
 * 2026-10-16 增强（详情页入口 + 暂停闭环）：
 *   - 新增 check prop：来自 GET /kickoff/{pid}/return-check 的实质开工判据明细，
 *     点「确认退回」前先把「阶段 / 巡检 / 分包报工 / 领料 / 分包开工日」摆给人看，避免误退真开工项目；
 *   - 已签分包合同的项目额外红字警示（签合同 ≠ 未进场，需人工确认）；
 *   - 退回后工程侧同步转入暂停，设计部重新移交时自动恢复 —— 文案同步说明。
 *
 * Props:
 *   - item: 卡片数据（至少含 id / address），即要被退回的工程项目
 *   - check: 可选，预检明细（{phase, phaseIndex, inspections, subCheckins, materials, subActualStart, subContracts}）
 *   - onClose(): 关闭弹窗
 *   - onDone(data): 退回成功回调，父组件据此刷新卡片列表
 */
export const ReturnToDesignDialog = ({ item, check, onClose, onDone }) => {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!reason.trim()) return toast.error(zh ? "请填写退回原因" : "Reason is required");
    setBusy(true);
    try {
      const { data } = await api.post(`/kickoff/${item.id}/return-design`, { reason: reason.trim() });
      // 未关联到设计项目时给出提示，避免工程经理误以为设计部已收到（数据异常场景）
      toast.success(data.linked
        ? (zh ? "已退回设计部：工程侧转入暂停、停止催办；设计侧状态回到「建筑审图中」" : "Returned to Design; project paused.")
        : (zh ? "已退回并暂停（未找到关联的设计项目，请人工同步设计部）" : "Returned & paused (no linked design project)"));
      onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="return-design-dialog">
        <div className="flex items-start gap-2">
          <RotateCcw className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-slate-900">{zh ? "退回设计部" : "Return to Design Dept."}</p>
            <p className="text-xs text-slate-500 mt-0.5" data-testid="return-design-address">{item.address}</p>
          </div>
        </div>
        <p className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
          <AlertTriangle className="h-3.5 w-3.5 inline mr-1 text-amber-600" />
          {zh
            ? <>项目将退回<b>设计部</b>，工作状态回到<b>「建筑审图中」</b>；本工程侧<b>同步转入暂停</b>（暂停时长不计入阶段停留），并停止开工倒计时与短信催办。设计部补办 Permit / 完善图纸后重新移交施工，<b>届时自动恢复开工</b>。适用于缺 Permit、图纸未就绪等无法开工的情况。</>
            : <>The project returns to <b>Design Dept.</b> (status → “Building In Plan Check”); this side is <b>paused</b> and countdown / SMS reminders stop. It <b>auto-resumes</b> when Design re-handoffs.</>}
        </p>
        {check && (
          <div className="text-[11px] rounded-lg border border-slate-200 bg-slate-50 p-2.5 space-y-1" data-testid="return-design-check">
            <p className="font-semibold text-slate-700">{zh ? "系统核查：判定为尚未开工" : "Pre-check: not started yet"}</p>
            <p className="text-slate-600" data-testid="return-design-check-detail">
              {zh ? "阶段" : "Phase"}：{check.phase || `phaseIndex ${check.phaseIndex}`}
              {" · "}{zh ? "巡检" : "Inspections"} {check.inspections}
              {" · "}{zh ? "分包报工" : "Sub reports"} {check.subCheckins}
              {" · "}{zh ? "领料" : "Materials"} {check.materials}
              {" · "}{zh ? "分包已报开工" : "Sub start dates"} {check.subActualStart}
            </p>
            {check.subContracts > 0 && (
              <p className="text-red-600 font-semibold" data-testid="return-design-sub-warning">
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                {zh ? `本项目已签 ${check.subContracts} 份分包合同（签合同 ≠ 未进场），请确认分包尚未开工再退回。` : `${check.subContracts} subcontract(s) signed — confirm subs have NOT started.`}
              </p>
            )}
          </div>
        )}
        <label className="text-xs text-slate-600 space-y-1 block">
          <span className="font-semibold">{zh ? "退回原因（必填）" : "Reason (required)"}</span>
          <textarea data-testid="return-design-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={300}
                    className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5" placeholder={zh ? "如：尚未拿到 Permit，无法开工" : "e.g. Permit not issued yet"} />
        </label>
        <div className="flex justify-end gap-2">
          <button data-testid="return-design-cancel" onClick={onClose} disabled={busy} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-60">{zh ? "取消" : "Cancel"}</button>
          <button data-testid="return-design-confirm" onClick={run} disabled={busy || !reason.trim()} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}{zh ? "确认退回" : "Return"}
          </button>
        </div>
      </div>
    </div>
  );
};
